import { Order } from '../Order/index.js'
import { UUID } from "uuidjs";
import cloneDeep from 'lodash.clonedeep'
import { OrderStates, OrderStatus } from '../consts.js'
import okeResponse from 'okeoke.standardresponse'
import { initDB, storeOrder, getDeviceOrdersAndUnsynced } from '../IDB/db'
import discounts from './discounts'

var Orders = function (client, clientSocket, catalog, app, offlineEnabled = false) {
  this.orders = {}

  this.uid = UUID.generate()

  this.deviceInfo = null

  this.inited = false
  this.offlineInited = false

  this.callbacks = {
    onOrdersInited: {},
    onOrderUpdated: {}, // Called when an order was updated
    onOrderChangedLocation: {},
    onOrderPrintSlips: {},
    onOrderItemStornoPrintSlips: {},
    onOrderPrintStornoSlips: {},
    onOrdersInitProgress: {},
    onOrderExternalOrderNumberReceived: {}
  }

  this.discounts = new discounts(this)

  this.singleBasketMode = () => {
    if (app === 'orderManager' || app === 'pos') return false
    return true
  }

  this.addCallback = (cbName, name, func) => {
    try {
      this.callbacks[cbName][name] = func
    } catch (e) {
      console.error('Invalid callback name in - add (Orders)', cbName)
    }
  }

  this.removeCallback = (cbName, name) => {
    try {
      delete this.callbacks[cbName][name]
    } catch (e) {
      console.error('Invalid callback name in - remove (Orders)', cbName)
    }
  }

  this.callCallbacks = async (cbName, message, reason) => {
    if (this.callbacks[cbName] == null) return
    for (let callback of Object.values(this.callbacks[cbName] || {})) {
      if (typeof callback === 'function') {
        try {
          callback(message)
        } catch (e) {
          console.error(e)
        }
      }
    }
  }

  const onOrderUpdated = (orderObj) => {
    if (app === 'pos') {
      storeOrder(orderObj.order).then((res) => {
        if (!res) {
          console.log('idb store order failed, will retry', orderObj.order.orderNumber, res)
          setTimeout(orderObj.save, 5000)
        }
      })
    }
    //console.log('Order updated', orderObj.waitingForExternalNumber, orderObj.order.orderNumber, orderObj.order.orderState)
    if (orderObj.waitingForExternalNumber && orderObj.order.orderNumber != null) {
      //console.log('Check for external number', orderObj.order.orderNumber)
      let splitNumber = orderObj.order.orderNumber.split('/')
      if(splitNumber.length === 4) {
        orderObj.waitingForExternalNumber = false
        this.callCallbacks('onOrderExternalOrderNumberReceived', { orderUUID: orderObj.order.uuid, externalOrderNumber: splitNumber[3] })
      }
    }
    if (orderObj.submitHappened && orderObj.order.orderNumber != null) {
      orderObj.submitHappened = false
    }
    if (orderObj.printSlips && orderObj.order.orderNumber != null) {
      orderObj.printSlips = false
      if (orderObj.order.orderState < OrderStates.CLOSED_SUCCESS) {
        orderObj.statuses.addStatus(OrderStatus.ORDER_PRINT_ONLINE_ORDER_SLIPS, { local: true })
        this.callCallbacks('onOrderPrintSlips', orderObj)
      } else {
        this.callCallbacks('onOrderPrintStornoSlips', orderObj)
      }
    }
    if (client.tokenData?.locationUUID != null && client.tokenData?.locationUUID !== orderObj.order?.locationUUID) {
      if (this.orders[orderObj.order.uuid] != null) {
        this.orders[orderObj.order.uuid].removeCallback('onOrderUpdated', 'Orders-' + this.uid)
        this.orders[orderObj.order.uuid].deinit()
        delete this.orders[orderObj.order.uuid]
      }
      this.callCallbacks('onOrderChangedLocation', orderObj)
    } else {
      this.orders[orderObj.order.uuid] = orderObj
      this.callCallbacks('onOrderUpdated', orderObj)
    }
  }

  const onCloudOrderUpdated = (order) => {
    if (client.tokenData.locationUUID == null || client.tokenData.locationUUID === order.locationUUID) {
      if (this.orders[order.uuid] == null) {
        let orderObj = new Order(client, clientSocket, catalog, offlineEnabled, app, this.deviceInfo)
        orderObj.addCallback('onOrderUpdated', 'Orders-' + this.uid, onOrderUpdated)
        orderObj.order = cloneDeep(order)
        onOrderUpdated(orderObj)
      }
    }
  }

  this.getOrder = async (uuid) => {
    if (this.orders[uuid]) {
      return okeResponse(true, 200, 'Ok. From memory.', this.orders[uuid])
    }
    let order = new Order(client, clientSocket, catalog, offlineEnabled, app, this.deviceInfo)
    let res = await order.load(uuid)
    if (res) {
      return okeResponse(true, 200, 'Ok.', order)
    } else {
      return okeResponse(false, 404, 'Order not found.', null)
    }
  }

  this.loadOrder = async (uuid, offlineOrder = null) => {
    if (this.orders[uuid] != null) return this.orders[uuid]
    let order = new Order(client, clientSocket, catalog, offlineEnabled, app, this.deviceInfo)
    if (offlineOrder != null) {
      order.addCallback('onOrderUpdated', 'Orders-' + this.uid, onOrderUpdated)
      if (order.loadOffline(offlineOrder)) {
        this.orders[uuid] = order
        //onOrderUpdated(order)
        return order
      } else {
        return null
      }
    } else {
      let res = await order.load(uuid)
      if (res) {
        order.addCallback('onOrderUpdated', 'Orders-' + this.uid, onOrderUpdated)
        this.orders[uuid] = order
        onOrderUpdated(order)
        return order
      } else {
        return null
      }
    }
  }

  this.getOrderAssembled = (uuid) => {
    if (this.orders[uuid]) {
      return { ...this.orders[uuid]?.order, assembledOrderItems: this.orders[uuid].assembledOrderItems }
    } else {
      return null
    }
  }

  const loadOrdersObj = async (ordersObjArr, message, cb) => {
    let i = 1
    for await (let order of ordersObjArr || []) {
      if (typeof cb === 'function') {
        cb({ message: message, total: ordersObjArr.length, current: i })
      }
      i++
      if (this.orders[order.uuid] != null) {
        if (!this.orders[order.uuid].offline) {
          //console.log('Merge online changes to order.', order.uuid)
          this.orders[order.uuid].loadFromIDB(order)
        } else {
          //console.log('Order was not save to cloud, saving and updating.', order.uuid)
          this.orders[order.uuid].save()
        }
      } else {
        //console.log('Load order.', order.uuid)
        await this.loadOrder(order.uuid)
      }
    }
    return true
  }

  this.findBasketOrder = () => {
    for (let orderObj of Object.values(this.orders || {}).reverse()) {
      if (orderObj.order.orderState === OrderStates.BASKET) {
        return orderObj
      }
    }
    return null
  }

  this.newOrder = (params = {}, getLastBasket = false) => {
    const { tableUUID, locationUUID, takeAway = false, sandbox = false, paymentType = null } = params
    if (!this.inited) return okeResponse(false, 401, 'NO - Orders component not ready yet.')
    if (this.singleBasketMode()) {
      let basketOrderObj = this.findBasketOrder()
      if (basketOrderObj != null) return okeResponse(true, 200, 'NO - Returning existing basket.', basketOrderObj)
    } else {
      if (getLastBasket) {
        let basketOrderObj = this.findBasketOrder()
        if (basketOrderObj != null) return okeResponse(true, 200, 'NO - Returning last basket.', basketOrderObj)
      }
    }
    let order = new Order(client, clientSocket, catalog, offlineEnabled, app, this.deviceInfo)
    order.addCallback('onOrderUpdated', 'Orders-' + this.uid, onOrderUpdated)
    let uuid = order.start(null, locationUUID, tableUUID, takeAway, sandbox, paymentType)
    this.orders[uuid] = order
    if (tableUUID) order.general.setTable(tableUUID)
    return okeResponse(true, 201, 'NO - Returning new basket.', order)
  }

  this.refreshOpenOrders = () => {
    return new Promise((resolve, reject) => {
      if (!clientSocket.authorized) {
        resolve(okeResponse(false, 503, 'Not connected', {}))
      } else {
        setTimeout(() => {
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }, 20000)
        clientSocket.ioclient.emit('getOpenOrders', {}, (data) => {
          resolve(data)
        })
      }
    })
  }

  this.refreshBaskets = () => {
    return new Promise((resolve, reject) => {
      if (!clientSocket.authorized) {
        resolve(okeResponse(false, 503, 'Not connected', {}))
      } else {
        setTimeout(() => {
          resolve({ success: false, message: 'Timeout!', code: 408, data: { errMsgCode: 'request.error.timeout' } })
        }, 20000)
        clientSocket.ioclient.emit('getBaskets', {}, (data) => {
          resolve(data)
        })
      }
    })
  }

  this.loadingCallback = (data) => {
    this.callCallbacks('onOrdersInitProgress', data)
  }

  this.refreshAll = () => {
    if (!this.offlineInited) return
    this.refreshOpenOrders().then(orders => {
      if (orders.success) {
        loadOrdersObj(orders.orders, 'init.validating.orders', this.loadingCallback).then(res => {
          this.refreshBaskets().then(orders => {
            if (orders.success) {
              loadOrdersObj(orders.baskets, 'init.validating.baskets', this.loadingCallback).then(res => {
                if (!this.inited) {
                  this.inited = true
                  this.callCallbacks('onOrdersInited', { inited: true })
                }
              })
            } else {
              console.error('Error refreshing baskets', orders)
              this.inited = true
              this.callCallbacks('onOrdersInited', { inited: true })
            }
          }).catch(e => {
            console.error('Error refreshing baskets', e)
            this.inited = true
            this.callCallbacks('onOrdersInited', { inited: true })
          })
        })
      } else {
        console.error('Error refreshing orders', orders)
        this.inited = true
        this.callCallbacks('onOrdersInited', { inited: true })
      }
    }).catch(e => {
      console.error('Error refreshing orders', e)
      this.inited = true
      this.callCallbacks('onOrdersInited', { inited: true })
    })
  }

  this.onConnectionChange = (data) => {
    switch (data.state) {
      case 'Authorized':
        this.refreshAll()
        break
      default:
        break
    }
  }

  this.initOffline = async (deviceUUID) => {
    if (deviceUUID != null && (this.deviceInfo?.deviceType != null && this.deviceInfo?.deviceType !== 1000)) {
      await initDB(deviceUUID)
      if (offlineEnabled) {
        let offlineOrders = await getDeviceOrdersAndUnsynced(deviceUUID)
        for await (let offlineOrder of offlineOrders || []) {
          //console.log('Load offline order.', offlineOrder.uuid)
          await this.loadOrder(offlineOrder.uuid, offlineOrder)
        }
      }
      this.offlineInited = true
    } else {
      if (app === 'brandApp') {
        this.offlineInited = true
      }
    }
    return true
  }

  this.init = (deviceInfo) => {
    if (app === 'secondScreen') {
      return null
    }
    this.deviceInfo = deviceInfo
    this.initOffline(deviceInfo?.deviceUUID).then(() => {
      clientSocket.addCallback('onConnectionChange', this.uid, this.onConnectionChange)
      clientSocket.addCallback('onOrderUpdated', 'Orders-' + this.uid, onCloudOrderUpdated)
      if (clientSocket.authorized) {
        this.refreshAll()
      } else {
        if (app !== 'brandApp') {
          this.inited = true
          this.callCallbacks('onOrdersInited', { inited: true })
        }
      }
    })
  }

  this.getOrders = () => {
    return this.orders
  }
}

export { Orders }
